import React, { useEffect, useState } from "react";
import { addNote, fetchNotes, updateNote, addComment, fetchComments} from "../api/api";
import { refreshTokenFunc } from "../api/auth"; // Import the refresh token function
import "./NotesPage.css"; // Import the stylesheet

function NotesPage({ token, setAuthToken }) {
  const [mainNote, setMainNote] = useState("");
  const [sharedWith, setSharedWith] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]); // Default to today's date
  const [commentInputs, setCommentInputs] = useState({}); // State for comment inputs for each note
  const [comments, setComments] = useState([]); // State for storing comments
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [error, setError] = useState("");

  const validateEmails = (emails) => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every((email) => emailRegex.test(email.trim()));
  };

  const handleInputChange = (e) => {
    setSharedWith(e.target.value);
    setError(""); // Clear error on change
  };

  const handleUnauthorized = async () => {
    try {
   
      const { accessToken, refreshToken, expiresIn, email } = await refreshTokenFunc(localStorage.getItem("refreshToken"));
      setAuthToken(accessToken);
      localStorage.setItem("authToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("tokenExpiry", Date.now() + expiresIn * 1000);
      localStorage.setItem("userEmail", email);
      return accessToken;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      setAuthToken(null);
      localStorage.clear();
      throw error;
    }
  };


  const loadNote = async (currentToken) => {
    try {
      const notes = await fetchNotes(selectedDate, currentToken); // Fetch notes for the selected date
      const note = notes.length > 0 ? notes[0] : null; // Assuming fetchNotes returns an array of notes
      if (note) {
        setMainNote(note.content);
        setSharedWith(note.shared_with.join(", "));
        setEditingNoteId(note.note_id);

        // Fetch comments for the note
        const fetchedComments = await fetchComments(note.note_id, currentToken);
        setComments(fetchedComments);
      
      } else {
        setMainNote("");
        setSharedWith("");
        setEditingNoteId(null);
        // setCommentInputs({});
        setComments([]);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        try {
          const newToken = await handleUnauthorized();
          await loadNote(newToken);
        } catch (refreshError) {
          console.error("Failed to refresh token and load note:", refreshError);
          setAuthToken(null);
          localStorage.clear();
        }
      } else {
        console.error("Error fetching notes:", error);
      }
    }
  };

  useEffect(() => {
    loadNote(token);
  }, [selectedDate, token]);

  const handleAddOrUpdateNote = async () => {
    if (!selectedDate) {
      alert("Please select a date for your note.");
      return;
    }

    const emailList = sharedWith.split(",").map((email) => email.trim());

    if (emailList === 0 | validateEmails(emailList)) {
      try {
        if (editingNoteId) {
          await updateNote(editingNoteId, mainNote, selectedDate, sharedWith.split(","),  token);
        } else {
          await addNote(mainNote, selectedDate, sharedWith.split(","), token);
        }

        const updatedNotes = await fetchNotes(selectedDate, token);
        const note = updatedNotes.length > 0 ? updatedNotes[0] : null;
        if (note) {
          setMainNote(note.content);
          setSharedWith(note.shared_with.join(", "));
          setEditingNoteId(note.note_id);

          // Fetch comments for the note
          const fetchedComments = await fetchComments(note.note_id, token);
          setComments(fetchedComments);        
        } else {
          setMainNote("");
          setSharedWith("");
          setEditingNoteId(null);
          // setCommentInputs({});
          setComments([]);
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          try {
            const newToken = await handleUnauthorized();
            await handleAddOrUpdateNote(newToken);
          } catch (refreshError) {
            console.error("Failed to refresh token and add/update note:", refreshError);
            setAuthToken(null);
            localStorage.clear();
          }
        } else {
          console.error("Error adding or updating note:", error);
        }
      }
    } else {
      setError("Invalid email(s) found. Please correct them.");
    }
  };

  const handleDateChange = (direction) => {
    const currentDate = new Date(selectedDate);
    const newDate = new Date(
      currentDate.setDate(currentDate.getDate() + (direction === "next" ? 1 : -1))
    )
      .toISOString()
      .split("T")[0];
    setSelectedDate(newDate);
  };

  const handleCommentChange = (note_id, value) => {
    setCommentInputs((prev) => ({ ...prev, [note_id]: value }));
  };

  const handleAddComment = async (note_id) => {
    const comment = commentInputs[note_id];
    if (!comment) {
      alert("Please enter a comment.");
      return;
    }

    try {
      await addComment(note_id, selectedDate, comment, token); // Call the API to add the comment
      setCommentInputs((prev) => ({ ...prev, [editingNoteId]: "" })); // Clear the comment input for that note

      const updatedComments = await fetchComments(note_id, token); // Refresh the comments
      setComments(updatedComments);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        try {
          const newToken = await handleUnauthorized();
          await handleAddComment(note_id, newToken);
        } catch (refreshError) {
          console.error("Failed to refresh token and add comment:", refreshError);
          setAuthToken(null);
          localStorage.clear();
        }
      } else {
        console.error("Error adding comment:", error);
      }
    }
  };

  return (
    <div className="notes-container">
      <div className="page-title">My Notes</div>
      {/* Date Selector */}
      <div className="date-selector">
        <button className="date-arrow" onClick={() => handleDateChange("prev")}>
          &#8592;
        </button>
        <div className="date-display">{selectedDate}</div>
        <button className="date-arrow" onClick={() => handleDateChange("next")}>
          &#8594;
        </button>
      </div>

      {/* Input Section */}
      <div className="note-input-section">
        <textarea
          value={mainNote}
          onChange={(e) => setMainNote(e.target.value)}
          placeholder="What went well today?"
          className="note-input"
        />
        <input
          value={sharedWith}
          onChange={handleInputChange}
          placeholder="Share with (comma-separated emails)"
          className="shared-with-input"
        />
        <button onClick={handleAddOrUpdateNote} className="add-note-button">
          {editingNoteId ? "Update Note" : "Add Note"}
        </button>
        {error && <p style={{ color: "red" }}>{error}</p>}
      </div>
      
       {/* Comments Section */}
      <div className="comments-section">
        {comments.length > 0 ? (
          <ul className="comments-list">
            <h3>Comments</h3>
            {comments.map((comment, index) => (
              <li key={index} className="comment-item">
                <div className="comment-header">
                  <span className="comment-author">{comment.commenter}</span>
                  <span className="comment-time">{new Date(comment.updated_at).toLocaleString()}</span>
                </div>
                <div className="comment-body">{comment.comment}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No comments yet. Be the first to comment!</p>
        )}
        <textarea
          className="comment-input"
          placeholder="Add a comment..."
          value={commentInputs[editingNoteId] || ""}
          onChange={(e) => handleCommentChange(editingNoteId, e.target.value)}
        />
        <button
          className="add-comment-button"
          onClick={() => handleAddComment(editingNoteId)}
        >
          Add Comment
        </button>
      </div>
    </div>
  );
}

export default NotesPage;